import { useTranslation } from 'react-i18next';
import Container from '@/components/ui/Container';
import SectionHeading from '@/components/ui/SectionHeading';
import { UserPlusIcon, QrCodeIcon, DownloadIcon } from '@/components/icons';
import { useInView } from '@/hooks/useInView';
import { ReactNode } from 'react';

const stepIcons = [UserPlusIcon, QrCodeIcon, DownloadIcon];

export default function HowItWorks() {
  const { t } = useTranslation();

  const steps = [1, 2, 3].map((num, index) => ({
    number: num,
    icon: stepIcons[index],
    title: t(`howItWorks.steps.${num}.title`),
    description: t(`howItWorks.steps.${num}.description`),
  }));

  return (
    <section id="how-it-works" className="py-20 md:py-28 bg-gradient-to-b from-white to-mk-pink-50">
      <Container>
        <SectionHeading
          title={t('howItWorks.title')}
          subtitle={t('howItWorks.subtitle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {steps.map((step, index) => (
            <StepCard key={step.number} {...step} delay={index * 0.15} />
          ))}
        </div>
      </Container>
    </section>
  );
}

interface StepCardProps {
  number: number;
  icon: (props: { className?: string }) => ReactNode;
  title: string;
  description: string;
  delay: number;
}

function StepCard({ number, icon: Icon, title, description, delay }: StepCardProps) {
  const { ref, isInView } = useInView(0.2);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`relative text-center transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {/* Step number badge */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 bg-mk-pink-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-lg">
        {number}
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl p-8 pt-10 shadow-lg hover:shadow-xl transition-shadow">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-mk-pink-100 to-mk-pink-200 rounded-2xl flex items-center justify-center">
          <Icon className="w-8 h-8 text-mk-pink-600" />
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
}
